import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { 
  Users, 
  KeyRound, 
  ArrowRight, 
  Loader2, 
  AlertCircle,
  ChevronLeft,
  BookOpen,
  Calendar,
  MessageSquare,
  Camera
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

/**
 * ParentLoginPage - Page de connexion pour les parents
 * Utilise les codes famille générés par l'enseignant (format ABC-123-XYZ)
 */
export default function ParentLoginPage() {
  const navigate = useNavigate();
  const { loginWithFamilyCode } = useAuth();
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Format code as user types (ABC-123-XYZ)
  const handleCodeChange = (e) => {
    let value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "");
    
    // Add dashes automatically
    if (value.length > 3) {
      value = value.slice(0, 3) + "-" + value.slice(3);
    }
    if (value.length > 7) {
      value = value.slice(0, 7) + "-" + value.slice(7);
    }
    
    // Limit to 11 characters (ABC-123-XYZ)
    value = value.slice(0, 11);
    
    setCode(value);
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate code format
    if (!/^[A-Z]{3}-[A-Z0-9]{3}-[A-Z0-9]{3}$/.test(code)) {
      setError("Format invalide. Le code doit être au format ABC-123-XYZ");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const result = await loginWithFamilyCode(code);
      
      toast.success(`Bienvenue ! Vous consultez le suivi de ${result.student_name}`);
      navigate("/parent/dashboard");
    } catch (err) {
      console.error("Login error:", err);
      if (err.response?.status === 404) {
        setError("Code invalide ou révoqué. Contactez l'enseignant.");
      } else {
        setError("Erreur de connexion. Veuillez réessayer.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-amber-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <button 
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-800 transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
            <span className="font-medium">Retour</span>
          </button>
          
          <div className="flex items-center gap-2">
            <img 
              src="/NaviLogo.png" 
              alt="Navi" 
              className="w-8 h-8"
            />
            <span className="font-bold text-slate-800">L'École de Navi</span>
          </div>
          
          <div className="w-20" /> {/* Spacer for centering */}
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-lg mx-auto px-4 py-12">
        {/* Welcome card */}
        <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Header illustration */}
          <div className="bg-gradient-to-r from-amber-400 to-orange-400 p-8 text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48Y2lyY2xlIGN4PSIyMCIgY3k9IjIwIiByPSIyIiBmaWxsPSJ3aGl0ZSIgZmlsbC1vcGFjaXR5PSIwLjIiLz48L3N2Zz4=')] opacity-50" />
            
            <div className="relative">
              <div className="w-20 h-20 bg-white rounded-2xl mx-auto flex items-center justify-center shadow-lg mb-4">
                <Users className="w-10 h-10 text-amber-500" />
              </div>
              <h1 className="text-2xl font-bold text-white">Espace Parents</h1>
              <p className="text-white/80 mt-2">
                Suivez la scolarité de votre enfant
              </p>
            </div>
          </div>

          {/* Login form */}
          <div className="p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Code famille
                </label>
                <div className="relative">
                  <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    type="text"
                    value={code}
                    onChange={handleCodeChange}
                    placeholder="ABC-123-XYZ"
                    className="pl-12 h-14 text-center text-xl tracking-widest font-mono uppercase border-2 focus:border-amber-400 focus:ring-amber-400"
                    autoComplete="off"
                    autoFocus
                  />
                </div>
                <p className="text-xs text-slate-500 mt-2 text-center">
                  Ce code vous a été transmis par l'enseignant
                </p>
              </div>

              {error && (
                <div className="flex items-center gap-2 text-red-600 bg-red-50 rounded-lg p-3">
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  <span className="text-sm">{error}</span>
                </div>
              )}

              <Button
                type="submit"
                disabled={loading || code.length < 11}
                className="w-full h-14 text-lg bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Connexion...
                  </>
                ) : (
                  <>
                    Accéder au suivi
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
            </form>

            {/* Features preview */}
            <div className="mt-8 pt-8 border-t border-slate-100">
              <p className="text-sm font-medium text-slate-700 text-center mb-4">
                Ce que vous pourrez consulter :
              </p>
              <div className="grid grid-cols-2 gap-3">
                <FeatureItem icon={MessageSquare} label="Messages" color="blue" />
                <FeatureItem icon={BookOpen} label="Devoirs" color="green" />
                <FeatureItem icon={Calendar} label="Réunions" color="purple" />
                <FeatureItem icon={Camera} label="Albums" color="amber" />
              </div>
            </div>
          </div>
        </div>

        {/* Help text */}
        <p className="text-center text-sm text-slate-500 mt-6">
          Vous n'avez pas de code ?{" "}
          <span className="text-amber-600 font-medium">
            Demandez-le à l'enseignant de votre enfant
          </span>
        </p>
      </main>
    </div>
  );
}

function FeatureItem({ icon: Icon, label, color }) {
  const colors = {
    blue: "bg-blue-100 text-blue-600",
    green: "bg-green-100 text-green-600",
    purple: "bg-purple-100 text-purple-600",
    amber: "bg-amber-100 text-amber-600",
  };

  return (
    <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-50">
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${colors[color]}`}>
        <Icon className="w-4 h-4" />
      </div>
      <span className="text-sm font-medium text-slate-700">{label}</span>
    </div>
  );
}
